import { type TabsItem } from "@cynthai/ui";

import {
  hasAgbwFeature,
  hasContinuousAuditorFeature,
  hasDocumentAnalysisFeature,
  hasFilesFeature,
  hasTasksFeature,
} from "@/core/lib/feature-guards";
import {
  projectPageHref,
  type ProjectModule,
} from "@/core/lib/project-modules";
import type { InputProcessStepKey } from "@/features/project/lib/input-process-steps";
import type { AppInfo } from "@/openapi";

export type ProjectTabKey =
  | ""
  | "input-process"
  | "validations"
  | "workflows"
  | "document-analysis"
  | "financial-explorer"
  | "agbw-overview"
  | "continuous-auditor-results";

export interface InputProcessVisibilityOptions {
  module: ProjectModule;
  features: string[];
  appCapabilities: Pick<AppInfo, "document_intelligence">;
  classificationTemplateIds?: string[];
  ledgerTaxonomyId?: string | null;
}

interface BuildProjectTabsOptions extends InputProcessVisibilityOptions {
  projectId: string;
  activeEntityProjectId?: string;
  labels: {
    overview: string;
    inputProcess: string;
    dataValidation: string;
    workflows: string;
    documentAnalysis: string;
    financialExplorer?: string;
    agbw: string;
    continuousAuditor: string;
  };
}

// what a module's projects need enabled before their workflows have data to
// run on
const WORKFLOW_FEATURE_RULES: Record<
  ProjectModule,
  (features: string[]) => boolean
> = {
  audit: (features) => hasTasksFeature({ features }),
  "corporate-finance": (features) =>
    features.includes("workflows") && features.includes("tasks_audit_file"),
  fas: (features) => features.includes("workflows"),
};

function hasProjectWorkflowFeature(
  module: ProjectModule,
  features: string[],
): boolean {
  return WORKFLOW_FEATURE_RULES[module](features);
}

function hasProjectFilesFeature(
  module: ProjectModule,
  features: string[],
): boolean {
  if (module === "audit") {
    return hasFilesFeature({ features });
  }

  return features.includes("tasks_audit_file");
}

function hasProjectDocumentsFeature(
  module: ProjectModule,
  features: string[],
  appCapabilities: Pick<AppInfo, "document_intelligence">,
): boolean {
  if (!appCapabilities?.document_intelligence) {
    return false;
  }

  if (module === "corporate-finance") {
    return (
      features.includes("statement_extraction") ||
      hasDocumentAnalysisFeature({ features })
    );
  }

  return (
    hasDocumentAnalysisFeature({ features }) ||
    hasProjectStatementExtractionFeature(features)
  );
}

function hasProjectStatementExtractionFeature(features: string[]): boolean {
  return features.includes("statement_extraction");
}

/** Audit and FAS always have input pages; CF needs a configured input step. */
export function hasInputProcessTab({
  module,
  features,
  appCapabilities,
}: InputProcessVisibilityOptions): boolean {
  if (module === "audit" || module === "fas") return true;

  return (
    hasProjectWorkflowFeature(module, features) ||
    hasProjectFilesFeature(module, features) ||
    hasProjectDocumentsFeature(module, features, appCapabilities) ||
    hasProjectStatementExtractionFeature(features)
  );
}

export function shouldShowInputProcessStep(
  step: InputProcessStepKey,
  {
    module,
    features,
    appCapabilities,
    classificationTemplateIds = [],
    ledgerTaxonomyId,
  }: InputProcessVisibilityOptions,
): boolean {
  const hasStatementExtraction = hasProjectStatementExtractionFeature(features);
  const hasWorkflow = hasProjectWorkflowFeature(module, features);
  const hasFiles = hasProjectFilesFeature(module, features);
  const hasDocuments = hasProjectDocumentsFeature(
    module,
    features,
    appCapabilities,
  );

  switch (step) {
    case "audit-files":
      return hasFiles;
    case "trial-balance":
      return module === "audit" && hasWorkflow;
    case "documents":
      return hasDocuments;
    case "classification":
      if (module === "fas") return true;

      if (module === "audit") {
        return (
          hasWorkflow &&
          (classificationTemplateIds.length > 0 || ledgerTaxonomyId != null)
        );
      }
      return hasStatementExtraction;
    case "statement-extraction":
      return hasStatementExtraction;
    case "reconciliation":
      if (module === "corporate-finance") return hasStatementExtraction;
      return module === "audit";
    default:
      return false;
  }
}

function hasProjectDocumentAnalysisFeature(
  features: string[],
  appCapabilities: Pick<AppInfo, "document_intelligence">,
): boolean {
  if (!appCapabilities?.document_intelligence) {
    return false;
  }

  return hasDocumentAnalysisFeature({ features });
}

export function buildProjectTabs({
  module,
  projectId,
  activeEntityProjectId,
  features,
  appCapabilities,
  classificationTemplateIds = [],
  labels,
}: BuildProjectTabsOptions): TabsItem<ProjectTabKey>[] {
  const hasWorkflowFeature = hasProjectWorkflowFeature(module, features);
  const hasDocAnalysis = hasProjectDocumentAnalysisFeature(
    features,
    appCapabilities,
  );
  const tabs: TabsItem<ProjectTabKey>[] = [];
  // a module only gets the tabs its routes serve
  const pushTab = (tab: ProjectTabKey, label: string) => {
    const href = projectPageHref(
      {
        module,
        projectId: activeEntityProjectId ?? projectId,
        rootProjectId: projectId,
      },
      tab,
    );
    if (href) {
      tabs.push({ label, value: tab, href });
    }
  };

  pushTab("", labels.overview);

  if (
    hasInputProcessTab({
      module,
      features,
      appCapabilities,
      classificationTemplateIds,
    })
  ) {
    pushTab("input-process", labels.inputProcess);
  }

  // data validation has no corporate finance implementation yet, so only
  // audit routes it; the rest of the old data tab now lives under input process
  if (hasWorkflowFeature) {
    pushTab("validations", labels.dataValidation);
  }

  if (hasWorkflowFeature) {
    pushTab("workflows", labels.workflows);
  }

  if (hasDocAnalysis) {
    pushTab("document-analysis", labels.documentAnalysis);
  }

  pushTab(
    "financial-explorer",
    labels.financialExplorer ?? "Financial explorer",
  );

  if (hasAgbwFeature({ features })) {
    pushTab("agbw-overview", labels.agbw);
  }

  if (hasContinuousAuditorFeature({ features })) {
    pushTab("continuous-auditor-results", labels.continuousAuditor);
  }

  return tabs;
}
